import {
  DotGraph,
  DotStatement,
  NodeStatement,
  EdgeStatement,
  Edge,
  Subgraph,
  GraphAttributes,
  NodeAttributes,
  EdgeAttributes,
  AttributeStatement,
  Assignment,
  parseLabel,
} from './types';

interface Token {
  type: string;
  value: string;
}

export class DotParser {
  private tokens: Token[] = [];
  private pos = 0;

  parse(source: string): DotGraph {
    this.tokens = this.tokenize(source);
    this.pos = 0;

    return this.parseGraph();
  }

  private tokenize(source: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;

    while (i < source.length) {
      if (/\s/.test(source[i]) || source[i] === '\n' || source[i] === '\r') {
        i++;
        continue;
      }

      if (source[i] === '/' && source[i + 1] === '/') {
        while (i < source.length && source[i] !== '\n') i++;
        continue;
      }

      if (source[i] === '/' && source[i + 1] === '*') {
        i += 2;
        while (i < source.length && !(source[i] === '*' && source[i + 1] === '/')) i++;
        i += 2;
        continue;
      }

      if (source[i] === '"') {
        i++;
        let str = '';
        while (i < source.length && source[i] !== '"') {
          if (source[i] === '\\' && i + 1 < source.length) {
            str += source[i + 1];
            i += 2;
          } else {
            str += source[i];
            i++;
          }
        }
        i++;
        tokens.push({ type: 'STRING', value: str });
        continue;
      }

      if (source[i] === '<') {
        const result = this.parseHtmlLikeString(source, i);
        tokens.push({ type: 'HTML_STRING', value: result.value });
        i = result.endPos;
        continue;
      }

      if (/[0-9]/.test(source[i])) {
        let num = '';
        let dotSeen = false;
        while (i < source.length && /[0-9.]/.test(source[i])) {
          if (source[i] === '.') {
            if (dotSeen) break;
            dotSeen = true;
          }
          num += source[i];
          i++;
        }
        tokens.push({ type: 'NUMERAL', value: num });
        continue;
      }

      if (/[a-zA-Z_]/.test(source[i])) {
        let id = '';
        while (i < source.length && /[a-zA-Z0-9_]/.test(source[i])) {
          id += source[i];
          i++;
        }
        const keyword = ['digraph', 'graph', 'subgraph', 'node', 'edge', 'strict', 'true', 'false'];
        const type = keyword.includes(id) ? id.toUpperCase() : 'ID';
        tokens.push({ type, value: id });
        continue;
      }

      const op = source.slice(i, i + 2);
      if (op === '->' || op === '--') {
        tokens.push({ type: 'EDGE_OP', value: op });
        i += 2;
        continue;
      }

      const d = source[i];
      if ('{}[]=;,'.includes(d)) {
        tokens.push({ type: d, value: d });
        i++;
        continue;
      }

      i++;
    }

    return tokens;
  }

  private parseHtmlLikeString(source: string, start: number): { value: string; endPos: number } {
    let pos = start;
    const tagStack: string[] = [];

    while (pos < source.length) {
      if (source[pos] === '<') {
        if (source[pos + 1] === '/') {
          const tagEnd = source.indexOf('>', pos);
          if (tagEnd === -1) throw new Error(`Unclosed tag at position ${pos}`);

          const closeTag = source.slice(pos + 2, tagEnd);
          if (tagStack.length === 0) {
            throw new Error(`Unexpected closing tag </${closeTag}>`);
          }

          const expectedTag = tagStack.pop()!;
          if (closeTag !== expectedTag) {
            throw new Error(`Mismatched tags: expected </${expectedTag}>, got </${closeTag}>`);
          }

          pos = tagEnd + 1;

          if (tagStack.length === 0) {
            return { value: source.slice(start, pos), endPos: pos };
          }
        } else {
          let tagPos = pos + 1;
          while (tagPos < source.length && /[\w\-:]/.test(source[tagPos])) {
            tagPos++;
          }
          const tagName = source.slice(pos + 1, tagPos);

          const tagEnd = source.indexOf('>', tagPos);
          if (tagEnd === -1) throw new Error(`Unclosed tag <${tagName} at position ${pos}`);

          const isSelfClosing = source[tagEnd - 1] === '/';

          if (!isSelfClosing) {
            tagStack.push(tagName);
          }

          pos = tagEnd + 1;
        }
      } else {
        pos++;
      }
    }

    throw new Error(`Unclosed HTML-like string starting at position ${start}`);
  }

  private mergeGraphAttributes(
    current: GraphAttributes,
    newAttrs: Record<string, string>
  ): GraphAttributes {
    const result: GraphAttributes = { ...current };

    for (const [key, value] of Object.entries(newAttrs)) {
      switch (key) {
        case 'rankdir':
          if (value === 'TB' || value === 'BT' || value === 'LR' || value === 'RL') {
            result.rankdir = value;
          }
          break;
        case 'rank':
          if (
            value === 'same' ||
            value === 'min' ||
            value === 'max' ||
            value === 'source' ||
            value === 'sink'
          ) {
            result.rank = value;
          }
          break;
        case 'ranksep':
        case 'nodesep':
        case 'margin':
        case 'fontsize':
          const num = parseFloat(value);
          if (!isNaN(num)) {
            (result as any)[key] = num;
          }
          break;
        case 'bgcolor':
        case 'label':
        case 'fontname':
        case 'style':
        case 'color':
          (result as any)[key] = value;
          break;
        // Other attributes ignored for now
      }
    }

    return result;
  }

  private extractGraphAttributes(statements: DotStatement[]): {
    filtered: DotStatement[];
    attributes: GraphAttributes;
  } {
    const filtered: DotStatement[] = [];
    let attributes: GraphAttributes = {};

    for (const stmt of statements) {
      if (stmt.type === 'attr' && stmt.target === 'graph') {
        attributes = this.mergeGraphAttributes(attributes, stmt.attributes);
      } else if (stmt.type === 'assignment') {
        // Assignment statements at graph level are graph attributes
        attributes = this.mergeGraphAttributes(attributes, { [stmt.name]: stmt.value });
      } else {
        filtered.push(stmt);
      }
    }

    return { filtered, attributes };
  }

  private parseGraph(): DotGraph {
    let strict = false;
    let type: 'digraph' | 'graph' = 'digraph';
    let id: string | undefined;

    if (this.peek()?.type === 'STRICT') {
      strict = true;
      this.advance();
    }

    const t = this.peek();
    if (t?.type === 'DIGRAPH' || t?.type === 'GRAPH') {
      type = t.type === 'DIGRAPH' ? 'digraph' : 'graph';
      this.advance();

      if (this.peek()?.type === 'ID') {
        id = this.advance().value;
      }
    }

    this.expect('{');
    const statements = this.parseStatements();
    this.expect('}');

    const { filtered, attributes } = this.extractGraphAttributes(statements);
    return { type, strict, id, statements: filtered, attributes };
  }

  private parseStatements(): DotStatement[] {
    const statements: DotStatement[] = [];

    while (this.peek()?.type !== '}' && this.peek() !== undefined) {
      statements.push(this.parseStatement());
      if (this.peek()?.type === ';') {
        this.advance();
      }
    }

    return statements;
  }

  private parseSubgraphOrEdgeStatement(): DotStatement {
    // Parse a subgraph (with optional 'subgraph' keyword)
    const subgraph = this.parseSubgraph();

    // Check if this subgraph is followed by an edge operator
    if (this.peek()?.type === 'EDGE_OP' || this.peek()?.type === '--') {
      // This is an edge statement starting with a subgraph
      const firstIds = this.extractNodeIdsFromSubgraph(subgraph);
      const edges = this.parseEdgeChain(firstIds);
      return { type: 'edge', edges };
    }

    // Otherwise, it's just a subgraph statement
    return subgraph;
  }

  private parseStatement(): DotStatement {
    const t = this.peek();

    if (t?.type === 'ID' || t?.type === 'STRING' || t?.type === 'HTML_STRING') {
      const next = this.tokens[this.pos + 1];

      if (next?.type === 'EDGE_OP' || next?.type === '--') {
        return this.parseEdgeStatement();
      } else if (next?.type === '[') {
        return this.parseNodeStatement();
      } else if (next?.type === '=') {
        return this.parseAssignment();
      }

      return this.parseNodeStatement();
    }

    if (t?.type === 'SUBDIGRAPH' || t?.type === 'SUBGRAPH' || t?.type === '{') {
      return this.parseSubgraphOrEdgeStatement();
    }

    if (t?.type === 'NODE' || t?.type === 'EDGE' || t?.type === 'GRAPH') {
      return this.parseAttrStatement();
    }

    throw new Error(`Unexpected token: ${t?.type}`);
  }

  private parseNodeStatement(): NodeStatement {
    const nodeId = this.expect('ID').value;
    let attributes: NodeAttributes = {};

    if (this.peek()?.type === '[') {
      attributes = this.parseAttributes();
    }

    return { type: 'node', nodeId, attributes };
  }

  private extractNodeIdsFromSubgraph(subgraph: Subgraph): string[] {
    const nodeIds: string[] = [];
    for (const stmt of subgraph.statements) {
      if (stmt.type === 'node') {
        nodeIds.push(stmt.nodeId);
      } else if (stmt.type === 'subgraph') {
        nodeIds.push(...this.extractNodeIdsFromSubgraph(stmt));
      }
      // edge statements inside subgraph are ignored for node extraction
    }
    return nodeIds;
  }

  private parseNodeIdOrSubgraph(): string[] {
    const t = this.peek();
    if (t?.type === '{' || t?.type === 'SUBDIGRAPH' || t?.type === 'SUBGRAPH') {
      const subgraph = this.parseSubgraph();
      return this.extractNodeIdsFromSubgraph(subgraph);
    } else {
      return [this.expect('ID').value];
    }
  }

  private parseEdgeChain(firstIds: string[]): Edge[] {
    const edges: Edge[] = [];
    let currentSources = firstIds;

    while (this.peek()?.type === 'EDGE_OP' || this.peek()?.type === '--') {
      this.advance();
      const targetIds = this.parseNodeIdOrSubgraph();

      let attrs: EdgeAttributes = {};
      if (this.peek()?.type === '[') {
        attrs = this.parseAttributes();
      }

      for (const source of currentSources) {
        for (const target of targetIds) {
          edges.push({ from: source, to: target, attributes: attrs });
        }
      }

      currentSources = targetIds;
    }

    return edges;
  }

  private parseEdgeStatement(): EdgeStatement {
    const firstIds = this.parseNodeIdOrSubgraph();
    const edges = this.parseEdgeChain(firstIds);
    return { type: 'edge', edges };
  }

  private parseSubgraph(): Subgraph {
    let id: string | undefined;

    if (this.peek()?.type === 'SUBDIGRAPH' || this.peek()?.type === 'SUBGRAPH') {
      this.advance();
      if (this.peek()?.type === 'ID') {
        id = this.advance().value;
      }
    }

    this.expect('{');
    const statements = this.parseStatements();
    this.expect('}');

    const { filtered, attributes } = this.extractGraphAttributes(statements);
    return { type: 'subgraph', id, statements: filtered, attributes };
  }

  private parseAttrStatement(): AttributeStatement {
    const target = this.expect(['NODE', 'EDGE', 'GRAPH']).value.toLowerCase() as
      | 'node'
      | 'edge'
      | 'graph';
    const attrs = this.parseAttributes();
    return { type: 'attr', target, attributes: attrs };
  }

  private parseAssignment(): Assignment {
    const name = this.expect('ID').value;
    this.expect('=');
    const {value} = this.expect(['STRING', 'HTML_STRING', 'ID', 'NUMERAL', 'TRUE', 'FALSE']);

    return { type: 'assignment', name, value };
  }

  private parseAttributes(): Record<string, string> {
    const attrs: Record<string, string> = {};

    this.expect('[');

    while (this.peek()?.type !== ']') {
      const name = this.expect('ID').value;
      this.expect('=');
      const valueToken = this.expect(['STRING', 'HTML_STRING', 'ID', 'NUMERAL', 'TRUE', 'FALSE']);

      attrs[name] = valueToken.value;

      if (this.peek()?.type === ',') {
        this.advance();
      }
    }

    this.expect(']');
    return attrs;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private advance(): Token {
    return this.tokens[this.pos++];
  }

  private expect(types: string | string[]): Token {
    const t = this.peek();
    const expected = Array.isArray(types) ? types : [types];

    if (!t || !expected.includes(t.type)) {
      throw new Error(`Expected ${types}, got ${t?.type}`);
    }

    return this.advance();
  }
}

export function parseDot(source: string): DotGraph {
  return new DotParser().parse(source);
}
